import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { supportApiClient } from "@/lib/api.ts";
import { useTimeout } from "@vueuse/core";

export type Link = {
  title: string;
  tag?: string;
  to?: string;
  href?: string;
  name?: string;
  startsWith?: string;
  target?: string;
};

type MenuItem = {
  text: string;
  href: string;
  target: string;
};

type MenuConfig = {
  menu?: {
    header?: MenuItem[];
    footer?: MenuItem[];
  };
  alerts?: unknown[];
  aliases?: string[];
  routes?: string[];
};

const Footer: Link[] = [
  {
    title: "FAQs",
    href: "https://support.simplybroadband.co.nz/faq",
    tag: "a",
  },
  { title: "Speed calculator", to: "/speed-calculator" },
  { title: "Complaints Policy", to: "/customer-complaints" },
  { title: "Privacy policy", to: "/privacy" },
  { title: "Contact us", to: "/contact-us" },
  { title: "Terms & Conditions", to: "/terms" },
];

const Top: Link[] = [
  {
    title: "Home",
    to: "/",
  },
  {
    title: "Speed calculator",
    to: "/speed-calculator",
  },
  {
    title: "Choose a plan",
    to: "/our-plans",
  },
  {
    title: "FAQ",
    href: "https://support.simplybroadband.co.nz/faq",
    tag: "a",
  },
  {
    title: "Contact us",
    to: "/contact-us",
  },
];

function convertMenu(menu: MenuItem[]): Link[] {
  return menu.map((item) => {
    return item.href[0] === "/"
      ? {
          title: item.text,
          to: item.href,
          target: item.target,
        }
      : {
          title: item.text,
          href: item.href,
          tag: "a",
          target: item.target,
        };
  });
}

export const useMenuStore = defineStore("menu", () => {
  const config = ref<MenuConfig>({});
  const { ready, stop } = useTimeout(1000, { controls: true });
  let configRequest: Promise<void> | null = null;

  const header = computed(() =>
    config.value?.menu?.header
      ? convertMenu(config.value.menu.header)
      : ready.value
        ? Top
        : [],
  );
  const footer = computed(() =>
    config.value?.menu?.footer ? convertMenu(config.value.menu.footer) : Footer,
  );

  const alerts = computed(() => config.value?.alerts || []);
  const aliases = computed(() => config.value?.routes || []);

  const fetchConfig = async (force = false): Promise<void> => {
    if (configRequest && !force) {
      return configRequest;
    }

    configRequest = supportApiClient
      .get<MenuConfig>("/v1/config")
      .then(({ data }) => {
        config.value = data;
        stop();
      })
      .catch(() => {
        // Keep existing aliases on transient failures.
      })
      .finally(() => {
        configRequest = null;
      });

    return configRequest;
  };

  void fetchConfig();

  return {
    header,
    footer,
    alerts,
    aliases,
    fetchConfig,
  };
});
